import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect, useMemo, useState } from "react";
import {
  adminDataApi,
  type ParticipantAssignmentInput,
  type ParticipantDto,
  type ParticipantInput,
  type ProgramDto
} from "../../services/admin-data-api";

const emptyParticipant: ParticipantInput = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  nationalId: ""
};

const emptyAssignment: ParticipantAssignmentInput = {
  programId: "",
  status: "ACTIVE"
};

export const ParticipantsPage = () => {
  const [participants, setParticipants] = useState<ParticipantDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [participantDialogOpen, setParticipantDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantDto | null>(null);
  const [participantForm, setParticipantForm] = useState<ParticipantInput>(emptyParticipant);
  const [assignmentForm, setAssignmentForm] = useState<ParticipantAssignmentInput>(emptyAssignment);
  const [isSaving, setIsSaving] = useState(false);

  // Enrollment edit state
  const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<{
    participantId: string;
    programId: string;
    programName: string;
    status: string;
  } | null>(null);
  const [enrollmentForm, setEnrollmentForm] = useState({
    status: "ACTIVE"
  });

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [participantData, programData] = await Promise.all([
        adminDataApi.getParticipants(),
        adminDataApi.getPrograms()
      ]);
      setParticipants(participantData);
      setPrograms(programData.filter((program) => program.status === "ACTIVE"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load participants");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch(() => {
      setError("Unable to load participants");
      setIsLoading(false);
    });
  }, []);

  const handleOpenCreate = () => {
    setSelectedParticipant(null);
    setParticipantForm(emptyParticipant);
    setParticipantDialogOpen(true);
  };

  const handleOpenEdit = (participant: ParticipantDto) => {
    setSelectedParticipant(participant);
    setParticipantForm({
      firstName: participant.firstName,
      lastName: participant.lastName,
      email: participant.email,
      phone: participant.phone ?? "",
      address: participant.address ?? "",
      notes: participant.notes ?? ""
    });
    setParticipantDialogOpen(true);
  };

  const handleCloseParticipantDialog = () => {
    setParticipantDialogOpen(false);
    setSelectedParticipant(null);
    setParticipantForm(emptyParticipant);
  };

  const handleSaveParticipant = async () => {
    setIsSaving(true);
    try {
      if (selectedParticipant) {
        await adminDataApi.updateParticipant(selectedParticipant.id, participantForm);
      } else {
        await adminDataApi.createParticipant(participantForm);
      }
      await loadData();
      handleCloseParticipantDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save participant");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAssignment = (participant: ParticipantDto) => {
    setSelectedParticipant(participant);
    setAssignmentForm({ ...emptyAssignment, programId: "" });
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false);
    setSelectedParticipant(null);
    setAssignmentForm(emptyAssignment);
  };

  const handleAssignProgram = async () => {
    if (!selectedParticipant || !assignmentForm.programId) {
      return;
    }
    setIsSaving(true);
    try {
      await adminDataApi.assignParticipantProgram(selectedParticipant.id, assignmentForm);
      await loadData();
      handleCloseAssignmentDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to assign program");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditEnrollment = (
    participantId: string,
    enrollment: { id: string; programId: string; program: { name: string }; status: string }
  ) => {
    setSelectedEnrollment({
      participantId,
      programId: enrollment.programId,
      programName: enrollment.program.name,
      status: enrollment.status
    });
    setEnrollmentForm({ status: enrollment.status });
    setEnrollmentDialogOpen(true);
  };

  const handleCloseEnrollmentDialog = () => {
    setEnrollmentDialogOpen(false);
    setSelectedEnrollment(null);
    setEnrollmentForm({ status: "ACTIVE" });
  };

  const handleUpdateEnrollment = async () => {
    if (!selectedEnrollment) return;
    setIsSaving(true);
    setError(null);
    try {
      await adminDataApi.updateParticipantProgram(
        selectedEnrollment.participantId,
        selectedEnrollment.programId,
        { status: enrollmentForm.status }
      );
      await loadData();
      handleCloseEnrollmentDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update enrollment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveEnrollment = async (participantId: string, programId: string) => {
    if (!window.confirm("Are you sure you want to remove this program enrollment?")) return;
    setError(null);
    try {
      await adminDataApi.removeParticipantProgram(participantId, programId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to remove enrollment");
    }
  };

  const availablePrograms = useMemo(() => {
    if (!selectedParticipant) {
      return programs;
    }
    const enrolledProgramIds = new Set(
      selectedParticipant.enrollments.map((enrollment) => enrollment.programId)
    );
    return programs.filter((program) => !enrolledProgramIds.has(program.id));
  }, [programs, selectedParticipant]);

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Grid item>
          <Typography variant="h4" component="h1">
            Participants
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage participants, keep contact information up to date, and assign them to programs.
          </Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleOpenCreate}>
            New Participant
          </Button>
        </Grid>
      </Grid>

      {isLoading ? <CircularProgress sx={{ display: "block", my: 4 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {participants.map((participant) => (
          <Box
            key={participant.id}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              p: 2,
              display: "flex",
              gap: 2,
              flexDirection: "column"
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">
                  {participant.firstName} {participant.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {participant.email}
                  {participant.phone ? ` • ${participant.phone}` : ""}
                </Typography>
                {participant.address ? (
                  <Typography variant="body2" color="text.secondary">
                    {participant.address}
                  </Typography>
                ) : null}
                {participant.nationalId ? (
                  <Typography variant="body2" color="text.secondary">
                    National ID: {participant.nationalId}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" onClick={() => handleOpenEdit(participant)}>
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleOpenAssignment(participant)}
                >
                  Assign Program
                </Button>
              </Stack>
            </Stack>
            {participant.enrollments.length ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {participant.enrollments.map((enrollment) => (
                  <Box
                    key={enrollment.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0.5
                    }}
                  >
                    <Chip
                      label={`${enrollment.program.name} • ${enrollment.status.toLowerCase()}`}
                      color={enrollment.status === "ACTIVE" ? "success" : "default"}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditEnrollment(participant.id, enrollment)}
                      title="Edit enrollment status"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveEnrollment(participant.id, enrollment.programId)}
                      title="Remove enrollment"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No program enrollments yet.
              </Typography>
            )}
          </Box>
        ))}
        {!isLoading && participants.length === 0 ? (
          <Alert severity="info">No participants yet. Use "New Participant" to get started.</Alert>
        ) : null}
      </Stack>

      <Dialog open={participantDialogOpen} onClose={handleCloseParticipantDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedParticipant ? "Edit Participant" : "Create Participant"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={participantForm.firstName}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, firstName: event.target.value }))
                }
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={participantForm.lastName}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, lastName: event.target.value }))
                }
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={participantForm.email}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, email: event.target.value }))
                }
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Phone"
                value={participantForm.phone ?? ""}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, phone: event.target.value }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Address"
                value={participantForm.address ?? ""}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, address: event.target.value }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="National ID"
                value={participantForm.nationalId ?? ""}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, nationalId: event.target.value }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={participantForm.notes ?? ""}
                onChange={(event) =>
                  setParticipantForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                multiline
                minRows={3}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseParticipantDialog}>Cancel</Button>
          <Button onClick={handleSaveParticipant} variant="contained" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignmentDialogOpen} onClose={handleCloseAssignmentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Program</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Program"
              value={assignmentForm.programId}
              onChange={(event) =>
                setAssignmentForm((prev) => ({ ...prev, programId: event.target.value }))
              }
              required
            >
              <option value="" disabled>
                Select a program
              </option>
              {availablePrograms.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </TextField>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Status"
              value={assignmentForm.status ?? "ACTIVE"}
              onChange={(event) =>
                setAssignmentForm((prev) => ({ ...prev, status: event.target.value }))
              }
            >
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="WITHDRAWN">Withdrawn</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignmentDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignProgram}
            disabled={isSaving || !assignmentForm.programId}
          >
            {isSaving ? "Assigning..." : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={enrollmentDialogOpen} onClose={handleCloseEnrollmentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Enrollment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Program: <strong>{selectedEnrollment?.programName}</strong>
            </Typography>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Status"
              value={enrollmentForm.status}
              onChange={(event) =>
                setEnrollmentForm((prev) => ({ ...prev, status: event.target.value }))
              }
            >
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="COMPLETED">Completed</option>
              <option value="WITHDRAWN">Withdrawn</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEnrollmentDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateEnrollment}
            disabled={isSaving}
          >
            {isSaving ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
